'use client';

import { Suspense, useCallback, useEffect, useRef } from 'react';

import { releaseCurrentPopup } from '@/activity-orchestration/lifecycle/release-current-popup';
import ErrorBoundary from '@/components/error-boundary';
import { isActivityShowable, useActivity } from '@/store/activity';
import { useActivityCurrent } from '@/store/activity-queue';

import { getPopupRenderer, preloadKnownPopupRenderers } from './registry';

import type { TDismissReason } from '@/activity-orchestration/types';

/** 按当前队列任务挂载 renderer，并处理过期、异常和超时关闭。 */

/** 防止 renderer 未关闭而永久占用队列。 */
const CURRENT_WATCHDOG_MS = 5 * 60 * 1000;

export default function ActivityPopupHost() {
    const current = useActivityCurrent();
    // 订阅当前弹窗对应活动：status / expireAt 变化时重跑校验，覆盖「已打开后中途过期」
    const activity = useActivity(current?.activityId ?? '');
    // current 看门狗定时器
    const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // 预取不占用当前弹窗。
    useEffect(() => {
        preloadKnownPopupRenderers();
    }, []);

    const clearWatchdog = useCallback(() => {
        if (watchdogRef.current) {
            clearTimeout(watchdogRef.current);
            watchdogRef.current = null;
        }
    }, []);

    const handleDismiss = useCallback(
        (reason: TDismissReason, taskId: string) => {
            clearWatchdog();
            releaseCurrentPopup(reason, taskId);
        },
        [clearWatchdog]
    );

    // 活动状态变化时重新校验，避免到期后仍可支付。
    useEffect(() => {
        clearWatchdog();

        if (!current) {
            return undefined;
        }

        const { id, type } = current;

        // 含：入队瞬间已过期，以及展示中 Store 定时器 expireActivity
        if (!isActivityShowable(activity)) {
            releaseCurrentPopup('expire', id);
            return undefined;
        }

        const Renderer = getPopupRenderer(type);
        if (!Renderer) {
            releaseCurrentPopup('error', id);
            return undefined;
        }

        watchdogRef.current = setTimeout(() => {
            handleDismiss('error', id);
        }, CURRENT_WATCHDOG_MS);

        return clearWatchdog;
        // activity 引用随 status/expireAt upsert 更新，从而驱动中途过期关窗
    }, [current, activity, clearWatchdog, handleDismiss]);

    if (!current) {
        return null;
    }

    // 过期瞬间先卸 UI，effect 内再 release 清 current（避免短暂仍可点支付）
    if (!isActivityShowable(activity)) {
        return null;
    }

    const Renderer = getPopupRenderer(current.type);
    if (!Renderer) {
        return null;
    }

    const { id, activityId } = current;

    return (
        <ErrorBoundary
            fallback={null}
            onError={() => {
                handleDismiss('error', id);
            }}
        >
            <Suspense fallback={null}>
                <Renderer activityId={activityId} onClose={reason => handleDismiss(reason, id)} />
            </Suspense>
        </ErrorBoundary>
    );
}
